import React, { useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { useDashboardDataProvider } from 'features/dashboard'
import { createRsrcKey } from 'features/resources'
import { SessionActivityCard } from 'procyon-ui'
import UsersResourcesActivitySkeleton from './UsersResourcesActivitySkeleton'

/* ------------------------------
 * Constants
 * ------------------------------ */
const YEAR_DISPLAY_FORMAT = 'MMM YYYY'
const THIRTY_DAYS_DISPLAY_FORMAT = 'DD MMM'
const SEVEN_DAYS_DISPLAY_FORMAT = 'DD MMM'
const TWENTY_FOUR_HOUR_DISPLAY_FORMAT = 'HH:00 A, DD MMM'

const EMPTY_DATASET = {
  '12m': [],
  '24h': [],
  '30d': [],
  '7d': []
}

/* ------------------------------
 * Date buckets (static)
 * ------------------------------ */
const LAST_12_MONTHS = []
let i = 11
while (i >= 0) {
  LAST_12_MONTHS.push(moment().subtract(i, 'months').format(YEAR_DISPLAY_FORMAT))
  i--
}

const LAST_30_DAYS = []
i = 30
while (i >= 0) {
  LAST_30_DAYS.push(moment().subtract(i, 'days').format(THIRTY_DAYS_DISPLAY_FORMAT))
  i--
}

const LAST_7_DAYS = []
i = 7
while (i >= 0) {
  LAST_7_DAYS.push(moment().subtract(i, 'days').format(SEVEN_DAYS_DISPLAY_FORMAT))
  i--
}

const LAST_24_HOURS_DATES_GROUPS = []
i = 24
while (i >= 0) {
  LAST_24_HOURS_DATES_GROUPS.push(
    moment().subtract(i, 'hours').format(TWENTY_FOUR_HOUR_DISPLAY_FORMAT)
  )
  i--
}

/* ------------------------------
 * Helpers
 * ------------------------------ */
const buildDateMaps = (heatMap) => {
  const twlvMthMap = { users: {}, resources: {} }
  const thirtyDaysMap = { users: {}, resources: {} }
  const sevenDaysMap = { users: {}, resources: {} }
  const twentyFourHoursMap = { users: {}, resources: {} }

  const groups = [Object.values(heatMap.users), Object.values(heatMap.resources)]
  const now = moment()

  groups.forEach((items, index) => {
    const mapKey = index === 0 ? 'users' : 'resources'

    items.forEach((data) => {
      const { user, rsrc, eventTimes } = data
      const refObj = mapKey === 'users' ? user : rsrc

      eventTimes.forEach((eventTime) => {
        const mmt = moment(eventTime)

        if (now.diff(mmt, 'months') < 12) {
          const yearKey = mmt.format(YEAR_DISPLAY_FORMAT)
          const existing = twlvMthMap[mapKey][yearKey]
          twlvMthMap[mapKey][yearKey] = {
            count: (existing?.count || 0) + 1,
            time: yearKey,
            dataKeys: [...(existing?.dataKeys || []), createRsrcKey(refObj)]
          }
        }

        if (now.diff(mmt, 'days') < 30) {
          const dayKey = mmt.format(THIRTY_DAYS_DISPLAY_FORMAT)
          const existing = thirtyDaysMap[mapKey][dayKey]
          thirtyDaysMap[mapKey][dayKey] = {
            count: (existing?.count || 0) + 1,
            time: dayKey,
            dataKeys: [...(existing?.dataKeys || []), createRsrcKey(refObj)]
          }
        }

        if (now.diff(mmt, 'days') < 7) {
          const sevenDayKey = mmt.format(SEVEN_DAYS_DISPLAY_FORMAT)
          const existing = sevenDaysMap[mapKey][sevenDayKey]
          sevenDaysMap[mapKey][sevenDayKey] = {
            count: (existing?.count || 0) + 1,
            time: sevenDayKey,
            dataKeys: [...(existing?.dataKeys || []), createRsrcKey(refObj)]
          }
        }

        if (now.diff(mmt, 'hours') < 24) {
          const hourKey = mmt.format(TWENTY_FOUR_HOUR_DISPLAY_FORMAT)
          const existing = twentyFourHoursMap[mapKey][hourKey]
          twentyFourHoursMap[mapKey][hourKey] = {
            count: (existing?.count || 0) + 1,
            time: hourKey,
            dataKeys: [...(existing?.dataKeys || []), createRsrcKey(refObj)]
          }
        }
      })
    })
  })

  return { twlvMthMap, thirtyDaysMap, sevenDaysMap, twentyFourHoursMap }
}

const buildSeries = (keys, map) =>
  keys.flatMap((time) => [
    { name: 'Users', time, amount: map.users[time]?.count || 0 },
    { name: 'Resources', time, amount: map.resources[time]?.count || 0 }
  ])

/* ------------------------------
 * Component
 * ------------------------------ */
const UsersResourcesActivity = () => {
  const { heatMap, startDays, overallLoading, dashboardReady } = useDashboardDataProvider()

  const [stableDataSet, setStableDataSet] = useState(EMPTY_DATASET)
  const hasAnimatedRef = useRef(false)

  /* ------------------------------
   * Data processing lifecycle
   * ------------------------------ */
  useEffect(() => {
    if (dashboardReady && heatMap) {
      const { twlvMthMap, thirtyDaysMap, sevenDaysMap, twentyFourHoursMap } =
        buildDateMaps(heatMap)

      setStableDataSet({
        '12m': buildSeries(LAST_12_MONTHS, twlvMthMap),
        '24h': buildSeries(LAST_24_HOURS_DATES_GROUPS, twentyFourHoursMap),
        '30d': buildSeries(LAST_30_DAYS, thirtyDaysMap),
        '7d': buildSeries(LAST_7_DAYS, sevenDaysMap)
      })

      if (!hasAnimatedRef.current) {
        hasAnimatedRef.current = true
      }
    } else {
      // Reset when loading or when data is not present
      setStableDataSet(EMPTY_DATASET)
      hasAnimatedRef.current = false
    }
  }, [dashboardReady, heatMap])

  /* ------------------------------
   * Render
   * ------------------------------ */

  const viewMap = {
    1: '24h',
    7: '7d',
    30: '30d',
    60: '30d',
    365: '12m'
  }

  const activeView = viewMap[startDays] || '7d'

  return (
    <div className='mt-8'>
      <SessionActivityCard
        title='Activity'
        caption='active users vs resources accessed'
        dataSet={stableDataSet}
        view={activeView}
        animate={!hasAnimatedRef.current}
      />
    </div>
  )
}

export { UsersResourcesActivity }
